export const OPERATIONS_CONTRACT_VERSION = "4C-C2";

export const OPERATIONAL_CATEGORIES = [
  "incidents",
  "errors",
  "queues",
  "refunds",
  "disputes",
  "internal_notes",
  "recovery",
] as const;

export type OperationalCategory =
  (typeof OPERATIONAL_CATEGORIES)[number];

export const INCIDENT_SEVERITIES = [
  "P0",
  "P1",
  "P2",
  "P3",
] as const;

export type IncidentSeverity =
  (typeof INCIDENT_SEVERITIES)[number];

export const INCIDENT_STATUSES = [
  "open",
  "acknowledged",
  "investigating",
  "monitoring",
  "resolved",
  "closed",
] as const;

export type IncidentStatus =
  (typeof INCIDENT_STATUSES)[number];

export const OPERATIONAL_QUEUE_STATUSES = [
  "pending",
  "processing",
  "retry_scheduled",
  "delivered",
  "dead_letter",
] as const;

export type OperationalQueueStatus =
  (typeof OPERATIONAL_QUEUE_STATUSES)[number];

export type OperationsRepository =
  | "EXPLORE"
  | "GIFTSHOP";

export interface OperationalSource {
  id: string;
  repository: OperationsRepository;
  category: OperationalCategory;
  sourceName: string;
  owner: string;
  readMode: "read_only";
  description: string;
}

export const OPERATIONAL_SOURCES:
  readonly OperationalSource[] = [
  {
    id: "explore-security-events",
    repository: "EXPLORE",
    category: "incidents",
    sourceName: "forfeo_security_events",
    owner: "Platform Security",
    readMode: "read_only",
    description:
      "Événements de sécurité et signaux opérationnels Explore.",
  },
  {
    id: "explore-smart-alerts",
    repository: "EXPLORE",
    category: "errors",
    sourceName: "smart_alert_events",
    owner: "Explore Operations",
    readMode: "read_only",
    description:
      "Alertes intelligentes et erreurs nécessitant une revue.",
  },
  {
    id: "explore-maintenance-tickets",
    repository: "EXPLORE",
    category: "incidents",
    sourceName: "habitat_maintenance_tickets",
    owner: "Habitat Support",
    readMode: "read_only",
    description:
      "Tickets opérationnels propriétaires et locataires.",
  },
  {
    id: "explore-refund-sync",
    repository: "EXPLORE",
    category: "refunds",
    sourceName: "explore_refund_sync_events",
    owner: "Explore Reconciliation",
    readMode: "read_only",
    description:
      "Synchronisation des remboursements reçus de Giftshop.",
  },
  {
    id: "explore-commission-events",
    repository: "EXPLORE",
    category: "refunds",
    sourceName: "forfeo_commission_events",
    owner: "Commission Operations",
    readMode: "read_only",
    description:
      "Effets de commissions et ajustements associés.",
  },
  {
    id: "giftshop-payment-events",
    repository: "GIFTSHOP",
    category: "errors",
    sourceName: "forfeo_payment_events",
    owner: "Giftshop Transactions",
    readMode: "read_only",
    description:
      "État canonique du traitement des événements de paiement.",
  },
  {
    id: "giftshop-refund-events",
    repository: "GIFTSHOP",
    category: "refunds",
    sourceName: "forfeo_refund_events",
    owner: "Giftshop Refunds",
    readMode: "read_only",
    description:
      "Événements canoniques de remboursement.",
  },
  {
    id: "giftshop-refund-effects",
    repository: "GIFTSHOP",
    category: "refunds",
    sourceName: "forfeo_refund_business_effects",
    owner: "Giftshop Refunds",
    readMode: "read_only",
    description:
      "Effets métier idempotents des remboursements.",
  },
  {
    id: "giftshop-refund-outbox",
    repository: "GIFTSHOP",
    category: "queues",
    sourceName: "forfeo_refund_delivery_outbox",
    owner: "Giftshop Delivery",
    readMode: "read_only",
    description:
      "Livraison retentable des remboursements vers Explore.",
  },
  {
    id: "explore-operations-cases",
    repository: "EXPLORE",
    category: "incidents",
    sourceName: "forfeo_operations_cases",
    owner: "Platform Operations",
    readMode: "read_only",
    description:
      "Registre central des incidents, litiges et dossiers de support.",
  },
  {
    id: "explore-operations-notes",
    repository: "EXPLORE",
    category: "internal_notes",
    sourceName: "forfeo_operations_case_notes",
    owner: "Platform Operations",
    readMode: "read_only",
    description:
      "Notes internes protégées liées aux dossiers opérationnels.",
  },
  {
    id: "explore-operations-events",
    repository: "EXPLORE",
    category: "recovery",
    sourceName: "forfeo_operations_case_events",
    owner: "Platform Operations",
    readMode: "read_only",
    description:
      "Journal immuable des créations, notes et transitions.",
  },
  {
    id: "explore-dispute-cases",
    repository: "EXPLORE",
    category: "disputes",
    sourceName: "forfeo_operations_cases",
    owner: "Platform Support",
    readMode: "read_only",
    description:
      "Litiges opérationnels sans mutation du moteur financier Giftshop.",
  },
];

export const OPERATIONS_GUARDRAILS = {
  consoleMode: "READ_ONLY",
  refundAuthority: "GIFTSHOP",
  financialMutationFromBrowser: "FORBIDDEN",
  refundActionMode: "ASSISTED_SERVER_WORKFLOW_ONLY",
  crossProjectMutation: "SIGNED_SERVER_TO_SERVER_ONLY",
  caseRegistry: "EXPLORE",
  disputeFinancialAuthority: "GIFTSHOP",
  internalNotesVisibility: "INTERNAL_ADMIN_ONLY",
  auditTrail: "APPEND_ONLY",
  remoteDeployment: "EXPLICIT_AUTHORIZATION_REQUIRED",
} as const;

export function getOperationalSources(
  repository?: OperationsRepository,
): readonly OperationalSource[] {
  if (!repository) return OPERATIONAL_SOURCES;

  return OPERATIONAL_SOURCES.filter(
    source => source.repository === repository,
  );
}
